import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ServiceType } from '@econtractor/types';
import * as apiService from 'api/apiServices';
import { useToast } from 'context/ToastContext';
import type { ServiceSelectOption } from '../types';
import SolarIcon from 'components/icons/SolarIcon';

export interface ServicesSelectProps {
    title?: string;
    onChange?: (option: ServiceSelectOption | null) => void;
    autoChangeId?: number | string | null;
    value?: number | string | null;
    disabled?: boolean;
    setIsLoading?: (loading: boolean) => void;
    required?: boolean;
    /** Лише монтування (без UI) — прогрів модуля / i18n */
    preload?: boolean;
}

const toOption = (service: ServiceType): ServiceSelectOption => ({
    value: service.ID,
    label: service.servName,
    article: service.servArticle,
    description: service.servDescription,
    defPrice: service.servDefPrice,
    defPriceSale: service.servDefPriceSale,
    dimensionName: service.dimensionName,
    dimensionI18nKey: service.dimensionI18nKey,
});

const ServiceNameItem = ({ service }: { service: ServiceSelectOption }) => {
    return (
        <div className="min-w-0 overflow-hidden flex-grow-1">
            <h6 className="mb-1 text-truncate" title={service.label}>
                {service.label || '—'}
            </h6>
            {service.article || service.description ? (
                <p className="f-12 mb-0 text-truncate" title={[service.article, service.description].filter(Boolean).join(' · ')}>
                    {service.article ? <span className="text-muted">{service.article}</span> : null}
                    {service.article && service.description ? <span className="text-muted"> · </span> : null}
                    {service.description ? <span className="text-muted">{service.description}</span> : null}
                </p>
            ) : null}
        </div>
    );
};

const ServicesSelect = ({
    title,
    onChange,
    autoChangeId,
    value,
    disabled = false,
    setIsLoading,
    required = true,
    preload = false,
}: ServicesSelectProps) => {
    const { t } = useTranslation(['Services', 'common']);
    const { showToast } = useToast();
    const [search, setSearch] = useState('');
    const [options, setOptions] = useState<ServiceSelectOption[]>([]);
    const [selected, setSelected] = useState<ServiceSelectOption | null>(null);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const selectedIdRef = useRef<number | string | null>(null);
    const autoAppliedRef = useRef<string | number | null>(null);
    const loadGenRef = useRef(0);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    selectedIdRef.current = selected?.value ?? null;

    const isFixed = Boolean(selected);
    const showResults = loading || searched;

    const setLoadingBoth = (next: boolean) => {
        setLoading(next);
        if (setIsLoading) setIsLoading(next);
    };

    const beginLoad = () => {
        loadGenRef.current += 1;
        const gen = loadGenRef.current;
        setLoadingBoth(true);
        return gen;
    };

    const endLoad = (gen: number) => {
        if (gen !== loadGenRef.current) return;
        setLoadingBoth(false);
    };

    const resolveServiceOption = async (serviceId: number | string): Promise<ServiceSelectOption | null> => {
        const normalizedId = Number(serviceId);
        if (!Number.isFinite(normalizedId) || normalizedId <= 0) return null;
        const service = await apiService.getService(normalizedId);
        return service ? toOption(service) : null;
    };

    const fetchServices = async () => {
        if (disabled || loading || isFixed) return;

        const gen = beginLoad();

        const res = await apiService.getList({
            search: (search || '').trim(),
            page: 1,
            onPage: 50,
            status: [1],
            order: 'order by serv_name',
        });

        if (gen !== loadGenRef.current) return;
        setSearched(true);
        endLoad(gen);

        if (!res) {
            setOptions([]);
            showToast(t('common:messError'), 'danger');
            return;
        }

        setOptions((res.services || []).map(toOption));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            fetchServices();
        }
    };

    const handleSelect = (option: ServiceSelectOption) => {
        if (disabled) return;
        setSelected(option);
        onChange?.(option);
        setSearch('');
        setOptions([]);
        setSearched(false);
    };

    const handleClear = () => {
        if (disabled) return;
        setSelected(null);
        onChange?.(null);
        setSearch('');
        setOptions([]);
        setSearched(false);
    };

    useEffect(() => {
        if (preload) return;

        const syncFromValue = async () => {
            if (value == null || value === '') {
                setSelected(null);
                return;
            }
            if (Number(selectedIdRef.current) === Number(value)) return;

            const gen = beginLoad();
            const option = await resolveServiceOption(value);
            if (gen !== loadGenRef.current) return;
            setSelected(option);
            endLoad(gen);
        };

        void syncFromValue();
    }, [value, preload]);

    useEffect(() => {
        if (preload) return;

        const normalizedAutoId = Number(autoChangeId);
        if (!Number.isFinite(normalizedAutoId) || normalizedAutoId <= 0) {
            autoAppliedRef.current = null;
            return;
        }
        if (Number(autoAppliedRef.current) === normalizedAutoId) return;

        autoAppliedRef.current = normalizedAutoId;

        const applyAuto = async () => {
            const gen = beginLoad();
            const option = await resolveServiceOption(normalizedAutoId);
            if (gen !== loadGenRef.current) return;
            setSelected(option);
            onChangeRef.current?.(option);
            endLoad(gen);
            if (!option) {
                showToast(t('selectNoResults'), 'warning');
            }
        };

        void applyAuto();
    }, [autoChangeId, preload]);

    if (preload) return null;

    const labelText = title || t('selectTitle');

    return (
        <div className="user-selected mb-3">
            {labelText && (
                <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label text-muted mb-1">{labelText}</label>
                </div>
            )}

            {isFixed ? (
                <div className="user-selected-fixed">
                    <div className="user-selected-fixed-user d-flex align-items-center gap-2 min-w-0">
                        <ServiceNameItem service={selected!} />
                    </div>
                    <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="user-selected-clear"
                        disabled={disabled}
                        title={t('common:buttons.clear')}
                        onClick={handleClear}
                    >
                        <SolarIcon name="x-lg" />
                    </Button>
                </div>
            ) : (
                <div className={`user-selected-panel${showResults ? ' is-open' : ''}`}>
                    <InputGroup className="user-selected-search">
                        <Form.Control
                            type="text"
                            value={search}
                            disabled={disabled || loading}
                            required={required}
                            placeholder={t('selectSearchPlaceholder')}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <Button
                            type="button"
                            variant="link"
                            className="user-selected-search-btn"
                            disabled={disabled || loading}
                            title={t('selectSearchBtn')}
                            onClick={fetchServices}
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                                <SolarIcon name="search" />
                            )}
                        </Button>
                    </InputGroup>

                    {showResults && (
                        <div className="user-selected-results position-relative" style={{ maxHeight: 220 }}>
                            {loading && (
                                <div className="user-selected-empty">
                                    <Spinner animation="border" size="sm" variant="primary" className="opacity-75" />
                                </div>
                            )}

                            {!loading && options.length === 0 && (
                                <div className="user-selected-empty">
                                    <SolarIcon name="briefcase" className="fs-5 d-block mb-1 opacity-50" />
                                    {t('selectNoResults')}
                                </div>
                            )}

                            {!loading && options.length > 0 && (
                                <ul className="user-selected-list">
                                    {options.map((option) => (
                                        <li key={option.value}>
                                            <button
                                                type="button"
                                                className="user-selected-item"
                                                disabled={disabled}
                                                onClick={() => handleSelect(option)}
                                            >
                                                <ServiceNameItem service={option} />
                                                <SolarIcon name="chevron-right" className="user-selected-item-chevron" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ServicesSelect;
